/** Dashboard layout constants (v3 only — “studio”); use appTheme for tokens. */
import { Platform } from 'react-native';
import { appTheme } from '..';
import type { ThemeColors } from '..';
import {
  androidDashboardHeaderCollapsedHeight,
  androidDashboardHeaderExpandedHeight,
  androidDashboardHeaderScrollThreshold,
  androidResolveShadow,
} from '../androidUiParity';

/** v3: rounded-rect / hub radius for cards and strips. */
export const DASH_STUDIO_RADIUS = 22;
/** v3: inner content inset inside cards and strips (aligns section titles and rows). */
export const DASH_STUDIO_INNER = appTheme.spacing.lg;

/**
 * Premium dashboard surface: soft depth only — no outline frame (v3: no border highlight on cards).
 * Used by: v3 `DashboardScreen`, `Dashboard*Card`, `DashboardScrollableGridCard`, `OnlineOrderImagesStrip`, `DashboardReminders` modals, `DashboardCommunityStrip`.
 */
export function studioCardShell(colors: ThemeColors) {
  return {
    backgroundColor: colors.surface,
    borderRadius: DASH_STUDIO_RADIUS,
    ...androidResolveShadow(appTheme.shadows.xs),
  };
}
/** Horizontal padding for dashboard scroll sections (screen edge gutter). */
export const DASHBOARD_PADDING_H = appTheme.spacing.lg;
/** Vertical padding for dashboard blocks. */
export const DASHBOARD_PADDING_V = appTheme.spacing.md;

/** Progress bar height (today + weekly macro charts). */
export const DASHBOARD_PROGRESS_STROKE = 8;

/** Gap between stacked sections (v3: even step). */
export const DASHBOARD_SECTION_GAP = appTheme.spacing.lg;

/**
 * `DashboardScrollableGridCard` max height for the community post row — fits `CommunityPostTile` (image + caption) + insets without clipping.
 */
export const DASHBOARD_COMMUNITY_GRID_MAX_HEIGHT = 400;

/** Header heights and scroll threshold. */
/** Tighter header: name, date, lang only (no subscription). */
/** iOS keeps taller expanded band — unscaled greeting/date + lang pill need more room than Android scale. */
export const DASHBOARD_HEADER_EXPANDED_HEIGHT = Platform.OS === 'android' ? androidDashboardHeaderExpandedHeight() : 88;
export const DASHBOARD_HEADER_COLLAPSED_HEIGHT = Platform.OS === 'android' ? androidDashboardHeaderCollapsedHeight() : 52;
export const DASHBOARD_HEADER_SCROLL_THRESHOLD = Platform.OS === 'android' ? androidDashboardHeaderScrollThreshold() : 100;

/** Card styling — legacy names kept for type parity; v3 uses `DASH_STUDIO_*` for visuals. */
export const DASHBOARD_CARD_PADDING = appTheme.card.padding.lg;
export const DASHBOARD_CARD_RADIUS = appTheme.card.radius;
export const DASHBOARD_CARD_SHADOW = appTheme.card.shadow;
export const DASHBOARD_CARD_BACKGROUND = appTheme.colors.surface;
export const DASHBOARD_CARD_BACKGROUND_FADE = appTheme.colors.backgroundFade;
